import { useCallback, useEffect, useRef, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import DashboardLayout from "../../controls/DashboardLayout";
import DashboardNavbar from "../../controls/DashboardNavbar";
import MDBox from "../../controls/MDBox";
import MDTypography from "../../controls/MDTypography";
import MDButton from "../../controls/MDButton";
import { Card } from "react-bootstrap";
import {
  Alert,
  Autocomplete,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { getData, postData } from "../../Utils/Http/api";

// Cómo se agrupan y se titulan las reglas en pantalla. El orden de los grupos es el de aparición en la tabla de DATA MUNI.
const GRUPOS = [
  {
    titulo: "Monto inicial",
    variables: ["MONTO"],
    tipo: "monto",
    agregable: true,
    ayuda:
      "Por rango: el monto del caso tiene que estar entre «Monto desde» y «Monto hasta» (los dos incluidos); sin «Monto hasta» no hay tope. Si un monto cae en más de un rango (por ejemplo, porque el «hasta» de uno es el «desde» del siguiente) cuenta solo el de «desde» más alto: no se suman.",
  },
  {
    titulo: "Cantidad de objetos",
    variables: ["CANTIDAD_OBJETOS"],
    tipo: "cantidad",
    agregable: true,
    ayuda:
      "«Igual a» suma cuando el caso tiene exactamente esa cantidad de objetos; «Desde», cuando tiene esa cantidad o más (cuenta solo el «Desde» más alto que se alcanza). Con el botón se agregan más renglones «Igual a».",
  },
  { titulo: "Situación del sujeto", variables: ["FALLECIDO", "CONCURSADO", "QUEBRADO"], ayuda: "Penalizan, pero el caso se propone igual si no hay otro mejor." },
  {
    titulo: "Tipo de objeto",
    variables: ["OBJETOS"],
    tipo: "objeto",
    agregable: true,
    ayuda: "Se busca el texto dentro de los objetos del caso, sin distinguir mayúsculas ni tildes. Se pueden acumular.",
  },
  {
    titulo: "Historial del sujeto",
    variables: ["HISTORIAL_SUJETO"],
    tipo: "historial",
    agregable: true,
    ayuda:
      "Sujetos con más de «x» casos, donde «x» es el número de la columna Valor, según cómo se cerró su caso anterior y cómo terminó su último evento. Con el botón se agrega un renglón para otro estado de la gestión (el de la pantalla Estados de la Gestión): el caso anterior tiene que haberse cerrado con incremento de deuda.",
  },
  {
    titulo: "Identificación (solo CUIT o CUIL de 11 dígitos)",
    variables: ["IDENTIFICACION"],
    ayuda: "El símbolo ? vale por cualquier dígito: 2?1 es «empieza por 2 y el tercer dígito es 1».",
  },
  { titulo: "Casos anteriores", variables: ["SIN_CASOS_ANTERIORES"], ayuda: "El sujeto no tuvo ningún caso antes." },
];

// Qué significa el valor de cada operador, para ponerlo como etiqueta del campo.
const ETIQUETA_OPERADOR = {
  IGUAL: "Igual a",
  MAYOR_IGUAL: "Desde",
  MAYOR_QUE: "Más de",
  CONTIENE: "Contiene",
  EMPIEZA_POR: "Empieza por",
};

const fechaLegible = (valor) => {
  if (!valor) return "todavía no se calculó";
  const fecha = new Date(valor);
  return Number.isNaN(fecha.getTime()) ? "-" : fecha.toLocaleString("es-AR");
};

// Etapa Pre-Judicial del catálogo de estados de la gestión.
const ETAPA_PREJUDICIAL = 1;

// «Mas de 5 casos…» → «Mas de x casos…»: el número es el valor de la regla (se cambia en la columna Valor), así que en la
// descripción va una «x». El backend ya lo hace; esto cubre el caso de un backend que todavía no lo hace.
const conX = (texto) => String(texto ?? "").replace(/^(M[aá]s de) \d+/i, "$1 x");

// Los montos se escriben con puntos de miles (150.000) y se envían solo con dígitos.
const aDigitos = (texto) => String(texto ?? "").replace(/\D/g, "");
const conMiles = (texto) => {
  const digitos = aDigitos(texto);
  return digitos === "" ? "" : Number(digitos).toLocaleString("es-AR");
};

function PuntajeReglaListar() {
  const [reglas, setReglas] = useState([]);
  // Renglones que se agregaron en pantalla y todavía no se guardaron: { clave, variable, valor, valorHasta, puntos }.
  const [nuevas, setNuevas] = useState([]);
  // Estados de la gestión para el renglón nuevo de Historial del sujeto: se piden la primera vez que hacen falta.
  const [estados, setEstados] = useState([]);
  const [cargandoEstados, setCargandoEstados] = useState(false);
  const pidioEstados = useRef(false);
  // Lo que se está editando, por IDPuntajeRegla: { valor, puntos, activo } como texto para poder escribir libremente.
  const [edicion, setEdicion] = useState({});
  const [puntajePrioritario, setPuntajePrioritario] = useState("100");
  const [diasEsperaGeneral, setDiasEsperaGeneral] = useState("1");
  const [resumen, setResumen] = useState({ casosConPuntaje: 0, ultimoCalculo: null, casosPendientes: 0 });
  const [recalculo, setRecalculo] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  // Interruptor general del Disparador de cara al gestor y su confirmación.
  const [disparadorActivo, setDisparadorActivo] = useState(false);
  const [confirmarActivo, setConfirmarActivo] = useState(false);
  const [cambiandoActivo, setCambiandoActivo] = useState(false);
  // Ventana del cálculo único de los casos sin puntaje (cerrados incluidos): cuántos hay y si se está iniciando.
  const [sinPuntajeAbierto, setSinPuntajeAbierto] = useState(false);
  const [sinPuntaje, setSinPuntaje] = useState(null);
  const [cargandoSinPuntaje, setCargandoSinPuntaje] = useState(false);
  const [iniciandoSinPuntaje, setIniciandoSinPuntaje] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, msg: "", severity: "success" });
  const temporizador = useRef(null);

  const aviso = (msg, severity = "success") => setSnackbar({ open: true, msg, severity });
  const mensajeDe = (ex, porDefecto) => ex?.detail || ex?.message || porDefecto;

  const cargar = useCallback(async () => {
    try {
      const res = await getData("/PuntajeConfiguracion");
      const datos = res?.value;
      const lista = Array.isArray(datos?.reglas) ? datos.reglas : [];
      setReglas(lista);
      setEdicion(
        Object.fromEntries(
          lista.map((r) => [r.idPuntajeRegla, { valor: r.valor ?? "", valorHasta: r.valorHasta ?? "", puntos: String(r.puntos), activo: !!r.activo }])
        )
      );
      setDisparadorActivo(datos?.disparadorActivo === true);
      setPuntajePrioritario(String(datos?.puntajePrioritario ?? 100));
      setDiasEsperaGeneral(String(datos?.diasEsperaGeneral ?? 1));
      setResumen({
        casosConPuntaje: datos?.casosConPuntaje ?? 0,
        ultimoCalculo: datos?.ultimoCalculo ?? null,
        casosPendientes: datos?.casosPendientes ?? 0,
      });
      setRecalculo(datos?.recalculo ?? null);
    } catch (ex) {
      console.error("Error cargando las reglas de puntaje:", ex);
      aviso(mensajeDe(ex, "No se pudieron cargar las reglas de puntaje"), "error");
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Mientras hay un recálculo en curso se consulta su avance cada 2 segundos; al terminar se recargan los datos.
  useEffect(() => {
    if (!recalculo?.ejecutando) return undefined;
    temporizador.current = setTimeout(async () => {
      try {
        const res = await getData("/PuntajeRecalculoEstado");
        const estado = res?.value;
        setRecalculo(estado);
        if (estado && !estado.ejecutando) {
          await cargar();
          if (estado.error) aviso(`El cálculo falló: ${estado.error}`, "error");
          else if (estado.total === 0) aviso(estado.incluyeCerrados ? "No había casos sin puntaje" : "No había casos pendientes: todos los puntajes están al día", "info");
          else aviso(`Listo: se calcularon ${estado.procesados.toLocaleString("es-AR")} casos`);
        }
      } catch (ex) {
        console.error("Error consultando el recálculo:", ex);
        setRecalculo((anterior) => (anterior ? { ...anterior, ejecutando: false } : anterior));
      }
    }, 2000);
    return () => clearTimeout(temporizador.current);
  }, [recalculo, cargar]);

  const cambiar = (id, campo, valor) => setEdicion((prev) => ({ ...prev, [id]: { ...prev[id], [campo]: valor } }));

  // Los estados de la gestión de Pre-Judicial (pantalla «Estados de la Gestión»), para elegir en qué estado terminó el caso anterior.
  const cargarEstados = async () => {
    if (pidioEstados.current) return;
    pidioEstados.current = true;
    setCargandoEstados(true);
    try {
      const res = await postData("/CasoEstadoListar", {});
      const lista = (res?.value || [])
        .filter((e) => e.idCasoEtapa === ETAPA_PREJUDICIAL && e.idCasoEstado !== 0)
        .map((e) => ({ idCasoEstado: e.idCasoEstado, descripcion: e.descripcion }))
        .sort((a, b) => (a.descripcion ?? "").localeCompare(b.descripcion ?? ""));
      setEstados(lista);
      // Si no vino ninguno se vuelve a intentar la próxima vez que se agregue un renglón de historial.
      pidioEstados.current = lista.length > 0;
      if (lista.length === 0) aviso("No se encontraron estados de la gestión para elegir", "warning");
    } catch (ex) {
      console.error("Error cargando los estados de la gestión:", ex);
      pidioEstados.current = false;
      aviso(mensajeDe(ex, "No se pudieron cargar los estados de la gestión"), "error");
    } finally {
      setCargandoEstados(false);
    }
  };

  // Renglones nuevos (un rango más de monto, un «igual a» más de cantidad de objetos, un «contiene» más de tipo de objeto o uno de
  // historial con su estado): viven en pantalla hasta que se guarda.
  const agregarNueva = (variable) => {
    setNuevas((prev) => [
      ...prev,
      { clave: `${Date.now()}-${prev.length}`, variable, valor: "", valorHasta: "", idCasoEstado: null, puntos: "" },
    ]);
    if (variable === "HISTORIAL_SUJETO") cargarEstados();
  };
  const cambiarNueva = (clave, campo, valor) => setNuevas((prev) => prev.map((n) => (n.clave === clave ? { ...n, [campo]: valor } : n)));
  const quitarNueva = (clave) => setNuevas((prev) => prev.filter((n) => n.clave !== clave));

  const guardar = async (recalcular) => {
    // Los puntos se validan acá para avisar antes de enviar; el resto de las validaciones las hace el backend.
    for (const r of reglas) {
      const puntos = parseInt(edicion[r.idPuntajeRegla]?.puntos, 10);
      if (Number.isNaN(puntos)) {
        aviso(`Los puntos de «${r.descripcion}» tienen que ser un número entero`, "warning");
        return;
      }
    }
    // Un renglón nuevo que quedó vacío se descarta; si tiene algo cargado, tiene que tener sus puntos.
    const nuevasCompletas = nuevas.filter((n) => n.valor !== "" || n.valorHasta !== "" || n.puntos !== "" || n.idCasoEstado !== null);
    for (const n of nuevasCompletas) {
      if (n.variable === "HISTORIAL_SUJETO" && n.idCasoEstado === null) {
        aviso("Elegí el estado del renglón nuevo de Historial del sujeto", "warning");
        return;
      }
      if (Number.isNaN(parseInt(n.puntos, 10))) {
        aviso("Completá los puntos del renglón nuevo (un número entero)", "warning");
        return;
      }
    }
    const prioritario = parseInt(puntajePrioritario, 10);
    if (Number.isNaN(prioritario)) {
      aviso("El puntaje de un caso prioritario tiene que ser un número entero", "warning");
      return;
    }

    const diasEspera = parseInt(diasEsperaGeneral, 10);
    if (Number.isNaN(diasEspera) || diasEspera < 0 || diasEspera > 365) {
      aviso("Los días de espera general tienen que ser un número entero de 0 a 365", "warning");
      return;
    }

    setGuardando(true);
    try {
      const res = await postData("/PuntajeConfiguracionGuardar", {
        reglas: reglas.map((r) => ({
          idPuntajeRegla: r.idPuntajeRegla,
          valor: edicion[r.idPuntajeRegla].valor,
          valorHasta: edicion[r.idPuntajeRegla].valorHasta,
          puntos: parseInt(edicion[r.idPuntajeRegla].puntos, 10),
          activo: edicion[r.idPuntajeRegla].activo,
        })),
        reglasNuevas: nuevasCompletas.map((n) => ({
          variable: n.variable,
          valor: n.valor,
          valorHasta: n.valorHasta,
          idCasoEstadoAnterior: n.idCasoEstado,
          puntos: parseInt(n.puntos, 10),
          activo: true,
        })),
        puntajePrioritario: prioritario,
        diasEsperaGeneral: diasEspera,
        recalcular: recalcular,
      });
      if (res?.isSuccess) {
        setNuevas([]);
        await cargar();
        const huboCambios = (res.value?.reglasModificadas ?? 0) > 0;
        const afectados = (res.value?.casosAfectados ?? 0).toLocaleString("es-AR");
        if (recalcular) {
          // Si ya había un cálculo en curso, el botón no lanza otro: se avisa.
          aviso(
            res.value?.recalculoIniciado
              ? huboCambios
                ? `Cambios guardados. Se vuelven a calcular los ${afectados} casos que esas reglas alcanzan…`
                : "No cambió ninguna regla. Se calculan solo los casos pendientes…"
              : "Cambios guardados (ya había un cálculo en curso)"
          );
        } else {
          aviso(
            huboCambios
              ? `Cambios guardados. ${afectados} casos quedaron pendientes (solo los que esas reglas alcanzan); para que se apliquen, calcular los pendientes.`
              : "No hay cambios para guardar",
            huboCambios ? "success" : "info"
          );
        }
      } else {
        aviso(res?.error || "No se pudieron guardar los cambios", "error");
      }
    } catch (ex) {
      aviso(mensajeDe(ex, "No se pudieron guardar los cambios"), "error");
    } finally {
      setGuardando(false);
    }
  };

  // Enciende o apaga el Disparador para los gestores. Apagarlo libera las propuestas en curso.
  const cambiarActivo = async () => {
    const activar = !disparadorActivo;
    setCambiandoActivo(true);
    try {
      const res = await postData("/DisparadorActivoGuardar", { activo: activar });
      if (res?.isSuccess) {
        setDisparadorActivo(activar);
        setConfirmarActivo(false);
        const liberadas = res.value?.propuestasLiberadas ?? 0;
        aviso(
          activar
            ? "Disparador activado: los gestores ya ven el panel y «Pedir un caso»"
            : liberadas > 0
              ? `Disparador desactivado. Se liberaron ${liberadas} ${liberadas === 1 ? "propuesta" : "propuestas"} en curso`
              : "Disparador desactivado: los gestores trabajan como antes"
        );
      } else {
        aviso(res?.error || "No se pudo cambiar el estado del Disparador", "error");
      }
    } catch (ex) {
      aviso(mensajeDe(ex, "No se pudo cambiar el estado del Disparador"), "error");
    } finally {
      setCambiandoActivo(false);
    }
  };

  const recalcular = async () => {
    try {
      const res = await postData("/PuntajeRecalcular", {});
      if (res?.isSuccess) {
        setRecalculo(res.value?.estado ?? null);
        aviso(res.value?.iniciado ? "Calculando los puntajes pendientes…" : "Ya hay un cálculo en curso", res.value?.iniciado ? "success" : "info");
      } else {
        aviso(res?.error || "No se pudo iniciar el cálculo", "error");
      }
    } catch (ex) {
      aviso(mensajeDe(ex, "No se pudo iniciar el cálculo"), "error");
    }
  };

  // Abre la ventana del cálculo único de los casos sin puntaje y trae cuántos hay, abiertos y cerrados.
  const abrirSinPuntaje = async () => {
    setSinPuntaje(null);
    setSinPuntajeAbierto(true);
    setCargandoSinPuntaje(true);
    try {
      const res = await getData("/PuntajeSinPuntajeResumen");
      setSinPuntaje(res?.value ?? null);
    } catch (ex) {
      setSinPuntajeAbierto(false);
      aviso(mensajeDe(ex, "No se pudo consultar cuántos casos no tienen puntaje"), "error");
    } finally {
      setCargandoSinPuntaje(false);
    }
  };

  const calcularSinPuntaje = async () => {
    setIniciandoSinPuntaje(true);
    try {
      const res = await postData("/PuntajeCalcularSinPuntaje", {});
      if (res?.isSuccess) {
        setRecalculo(res.value?.estado ?? null);
        setSinPuntajeAbierto(false);
        aviso(
          res.value?.iniciado ? "Calculando los puntajes de los casos que no tienen…" : "Ya hay un cálculo en curso",
          res.value?.iniciado ? "success" : "info"
        );
      } else {
        aviso(res?.error || "No se pudo iniciar el cálculo", "error");
      }
    } catch (ex) {
      aviso(mensajeDe(ex, "No se pudo iniciar el cálculo"), "error");
    } finally {
      setIniciandoSinPuntaje(false);
    }
  };

  const ejecutando = !!recalculo?.ejecutando;
  const sinPuntajeTotal = (sinPuntaje?.abiertos ?? 0) + (sinPuntaje?.cerrados ?? 0);
  const porcentaje = recalculo?.total > 0 ? Math.min(100, Math.round((recalculo.procesados / recalculo.total) * 100)) : 0;

  // «Monto desde» / «Monto hasta»: se escriben con puntos de miles (xxx.xxx).
  const campoMonto = (etiqueta, valor, alCambiar) => (
    <TextField
      size="small"
      variant="standard"
      label={etiqueta}
      placeholder="xxx.xxx"
      value={conMiles(valor)}
      onChange={(ev) => alCambiar(aDigitos(ev.target.value))}
      inputProps={{ inputMode: "numeric" }}
      sx={{ minWidth: 150, flex: 1 }}
    />
  );

  const celdaPuntos = (puntos, alCambiar) => (
    <TableCell sx={{ width: "16%" }}>
      <TextField size="small" variant="standard" type="number" label="Puntos" value={puntos} onChange={(ev) => alCambiar(ev.target.value)} fullWidth />
    </TableCell>
  );

  const filaRegla = (r) => {
    const e = edicion[r.idPuntajeRegla] ?? { valor: "", valorHasta: "", puntos: "0", activo: true };
    const esMonto = r.variable === "MONTO";
    const esObjeto = r.variable === "OBJETOS";
    const esCantidad = r.variable === "CANTIDAD_OBJETOS";
    return (
      <TableRow key={r.idPuntajeRegla} sx={{ opacity: e.activo ? 1 : 0.55 }}>
        {esMonto ? (
          <TableCell colSpan={2}>
            <MDBox display="flex" gap={3} flexWrap="wrap">
              {campoMonto("Monto desde", e.valor, (v) => cambiar(r.idPuntajeRegla, "valor", v))}
              {campoMonto("Monto hasta", e.valorHasta, (v) => cambiar(r.idPuntajeRegla, "valorHasta", v))}
            </MDBox>
          </TableCell>
        ) : (
          <>
            <TableCell sx={{ width: "46%" }}>
              <MDTypography variant="caption" color="text" fontWeight="medium">
                {esObjeto ? "Contiene" : esCantidad ? "Cantidad de Objetos" : r.variable === "HISTORIAL_SUJETO" ? conX(r.descripcion) : r.descripcion}
              </MDTypography>
            </TableCell>
            <TableCell sx={{ width: "22%" }}>
              {r.operador === "VERDADERO" ? (
                <MDTypography variant="caption" color="text">
                  Si se cumple
                </MDTypography>
              ) : (
                <TextField
                  size="small"
                  variant="standard"
                  label={esObjeto ? "Tipo de objeto" : (ETIQUETA_OPERADOR[r.operador] ?? "Valor")}
                  value={e.valor}
                  onChange={(ev) => cambiar(r.idPuntajeRegla, "valor", esCantidad ? aDigitos(ev.target.value) : ev.target.value)}
                  fullWidth
                />
              )}
            </TableCell>
          </>
        )}
        {celdaPuntos(e.puntos, (v) => cambiar(r.idPuntajeRegla, "puntos", v))}
        <TableCell align="center" sx={{ width: "16%" }}>
          <Checkbox checked={e.activo} onChange={(ev) => cambiar(r.idPuntajeRegla, "activo", ev.target.checked)} />
        </TableCell>
      </TableRow>
    );
  };

  // Las dos primeras celdas de un renglón nuevo, según de qué grupo es. Cantidad de objetos es siempre «Igual a»; en Historial del
  // sujeto se elige el estado de la gestión en que terminó el caso anterior (la «x» de «Más de x casos» es el número de la segunda celda).
  const celdasNueva = (n) => {
    if (n.variable === "MONTO") {
      return (
        <TableCell colSpan={2}>
          <MDBox display="flex" gap={3} flexWrap="wrap">
            {campoMonto("Monto desde", n.valor, (v) => cambiarNueva(n.clave, "valor", v))}
            {campoMonto("Monto hasta", n.valorHasta, (v) => cambiarNueva(n.clave, "valorHasta", v))}
          </MDBox>
        </TableCell>
      );
    }
    if (n.variable === "HISTORIAL_SUJETO") {
      return (
        <>
          <TableCell sx={{ width: "46%" }}>
            <MDTypography variant="caption" color="text" fontWeight="medium" display="block">
              Más de x casos, caso anterior con incremento de deuda y último evento
            </MDTypography>
            <Autocomplete
              size="small"
              options={estados}
              loading={cargandoEstados}
              loadingText="Cargando estados…"
              noOptionsText="No hay estados"
              getOptionLabel={(estado) => estado.descripcion ?? ""}
              isOptionEqualToValue={(opcion, valor) => opcion.idCasoEstado === valor.idCasoEstado}
              value={estados.find((estado) => estado.idCasoEstado === n.idCasoEstado) ?? null}
              onChange={(ev, estado) => cambiarNueva(n.clave, "idCasoEstado", estado?.idCasoEstado ?? null)}
              renderInput={(params) => <TextField {...params} variant="standard" label="Estado de la gestión" />}
            />
          </TableCell>
          <TableCell sx={{ width: "22%" }}>
            <TextField
              size="small"
              variant="standard"
              label={ETIQUETA_OPERADOR.MAYOR_QUE}
              value={n.valor}
              onChange={(ev) => cambiarNueva(n.clave, "valor", aDigitos(ev.target.value))}
              fullWidth
            />
          </TableCell>
        </>
      );
    }
    const esCantidad = n.variable === "CANTIDAD_OBJETOS";
    return (
      <>
        <TableCell sx={{ width: "46%" }}>
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {esCantidad ? "Cantidad de Objetos" : "Contiene"}
          </MDTypography>
        </TableCell>
        <TableCell sx={{ width: "22%" }}>
          <TextField
            size="small"
            variant="standard"
            label={esCantidad ? ETIQUETA_OPERADOR.IGUAL : "Tipo de objeto"}
            value={n.valor}
            onChange={(ev) => cambiarNueva(n.clave, "valor", esCantidad ? aDigitos(ev.target.value) : ev.target.value)}
            fullWidth
          />
        </TableCell>
      </>
    );
  };

  // Un renglón agregado en pantalla que todavía no se guardó: igual que los demás, pero con un botón para quitarlo.
  const filaNueva = (n) => (
    <TableRow key={n.clave} sx={{ backgroundColor: "rgba(25, 118, 210, 0.06)" }}>
      {celdasNueva(n)}
      {celdaPuntos(n.puntos, (v) => cambiarNueva(n.clave, "puntos", v))}
      <TableCell align="center" sx={{ width: "16%" }}>
        <Tooltip title="Quitar este renglón">
          <IconButton size="small" aria-label="Quitar el renglón nuevo" onClick={() => quitarNueva(n.clave)}>
            <Close fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="secondary" borderRadius="lg" coloredShadow="light">
                <MDTypography variant="h6" color="white">
                  Reglas de Puntaje - Disparador
                </MDTypography>
                <MDTypography variant="button" color="white" fontWeight="regular">
                  Disparador Automático de Gestiones: el puntaje de un caso es la suma de las reglas que cumple.
                </MDTypography>
              </MDBox>

              <MDBox pt={3} pb={3} px={2}>
                {!cargando && (
                  <Alert
                    severity={disparadorActivo ? "success" : "warning"}
                    sx={{ mb: 2, alignItems: "center" }}
                    action={
                      <MDButton
                        variant="contained"
                        color={disparadorActivo ? "warning" : "success"}
                        size="small"
                        onClick={() => setConfirmarActivo(true)}
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        {disparadorActivo ? "Desactivar" : "Activar"}
                      </MDButton>
                    }
                  >
                    <strong>El Disparador está {disparadorActivo ? "activo" : "desactivado"} para los gestores.</strong>{" "}
                    {disparadorActivo
                      ? "Ven el panel del caso asignado y el botón «Pedir un caso», y quienes son Obligatorios o Mixtos reciben los casos automáticamente."
                      : "Trabajan como siempre: no ven el panel ni «Pedir un caso» y no se les asigna ningún caso. Mientras tanto se pueden dejar listas las reglas, la modalidad de cada usuario y los tipos de evento."}
                  </Alert>
                )}

                <Alert severity="info" sx={{ mb: 2 }}>
                  Un caso se calcula una sola vez: el puntaje no se vuelve a calcular por más que el caso cambie de estado. Solo
                  se calculan los casos <strong>pendientes</strong>: los que todavía no tienen puntaje (por ejemplo, los que
                  entran al importar) y los que alcanza una regla que se cambió en esta pantalla. Cambiar una regla no
                  recalcula todos los casos: solo los que esa regla cumplía o cumple con su valor nuevo (por ejemplo, subir los
                  puntos de «Contiene automotor» alcanza solo a los casos con automotor). Los casos nuevos se calculan solos
                  al importar casos y al cargar el cierre. «Calcular pendientes» solo toma los casos sin cerrar: un caso cerrado,
                  con cualquier estado de cierre, ya no se vuelve a trabajar. Los casos cerrados que no tienen puntaje se calculan
                  una sola vez desde «Casos cerrados sin puntaje». En Monto inicial y en Tipo de objeto se puede agregar un renglón
                  más con «Agregar renglón»: queda guardado con <strong>Guardar</strong>.
                </Alert>

                <MDBox display="flex" flexWrap="wrap" alignItems="center" gap={2} mb={2}>
                  <MDTypography variant="button" color="text" fontWeight="regular">
                    Último cálculo: <strong>{fechaLegible(resumen.ultimoCalculo)}</strong> · Casos con puntaje:{" "}
                    <strong>{resumen.casosConPuntaje.toLocaleString("es-AR")}</strong> · Pendientes de calcular:{" "}
                    <strong>{resumen.casosPendientes.toLocaleString("es-AR")}</strong>
                  </MDTypography>
                  <MDButton variant="gradient" color="info" size="small" onClick={recalcular} disabled={ejecutando || cargando || resumen.casosPendientes === 0}>
                    Calcular pendientes
                  </MDButton>
                  <MDButton variant="text" color="info" size="small" onClick={abrirSinPuntaje} disabled={ejecutando || cargando}>
                    Casos cerrados sin puntaje…
                  </MDButton>
                </MDBox>

                {ejecutando && (
                  <MDBox mb={2}>
                    <LinearProgress variant={recalculo.total > 0 ? "determinate" : "indeterminate"} value={porcentaje} />
                    <MDTypography variant="caption" color="text">
                      {recalculo.incluyeCerrados ? "Calculando los casos sin puntaje (cerrados incluidos)… " : "Calculando… "}
                      {recalculo.procesados.toLocaleString("es-AR")} de {recalculo.total.toLocaleString("es-AR")} casos
                    </MDTypography>
                  </MDBox>
                )}
                {!ejecutando && recalculo?.error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    El último cálculo falló: {recalculo.error}
                  </Alert>
                )}

                <MDBox mb={3} display="flex" flexWrap="wrap" gap={4}>
                  <MDBox width={420} maxWidth="100%">
                    <TextField
                      size="small"
                      variant="standard"
                      type="number"
                      label="Puntos que suma un caso marcado como prioritario"
                      helperText="Tiene que ser mayor que cualquier puntaje natural para que el caso pase primero."
                      value={puntajePrioritario}
                      onChange={(ev) => setPuntajePrioritario(ev.target.value)}
                      fullWidth
                    />
                  </MDBox>
                  <MDBox width={420} maxWidth="100%">
                    <TextField
                      size="small"
                      variant="standard"
                      type="number"
                      label="Días de espera general para volver a proponer un caso"
                      helperText="Lo usan los tipos de evento que no tienen días propios (pantalla Tipos de Evento)."
                      value={diasEsperaGeneral}
                      onChange={(ev) => setDiasEsperaGeneral(ev.target.value)}
                      fullWidth
                    />
                  </MDBox>
                </MDBox>

                {GRUPOS.map((grupo) => {
                  const filas = reglas.filter((r) => grupo.variables.includes(r.variable));
                  const nuevasDelGrupo = nuevas.filter((n) => grupo.variables.includes(n.variable));
                  if (filas.length === 0 && nuevasDelGrupo.length === 0) return null;
                  return (
                    <MDBox key={grupo.titulo} mb={3}>
                      <MDTypography variant="h6" fontWeight="medium">
                        {grupo.titulo}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" display="block" mb={1}>
                        {grupo.ayuda}
                      </MDTypography>
                      <TableContainer>
                        <Table size="small">
                          {/* El tema del proyecto le pone display:block al TableHead y el encabezado se sale de las columnas del cuerpo. */}
                          <TableHead sx={{ display: "table-header-group", p: 0 }}>
                            <TableRow>
                              {grupo.tipo === "monto" ? (
                                <TableCell colSpan={2} sx={{ width: "68%" }}>
                                  Rango de monto
                                </TableCell>
                              ) : (
                                <>
                                  <TableCell sx={{ width: "46%" }}>Regla</TableCell>
                                  <TableCell sx={{ width: "22%" }}>Valor</TableCell>
                                </>
                              )}
                              <TableCell sx={{ width: "16%" }}>Puntos</TableCell>
                              <TableCell align="center" sx={{ width: "16%" }}>
                                Activa
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filas.map(filaRegla)}
                            {nuevasDelGrupo.map(filaNueva)}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      {grupo.agregable && (
                        <MDButton variant="text" color="info" size="small" onClick={() => agregarNueva(grupo.variables[0])} disabled={guardando || cargando}>
                          + Agregar renglón
                        </MDButton>
                      )}
                    </MDBox>
                  );
                })}

                {!cargando && reglas.length === 0 && (
                  <Alert severity="warning">
                    No hay reglas cargadas. Falta correr el script <code>disparador_puntaje.sql</code> en la base.
                  </Alert>
                )}

                <MDBox display="flex" gap={2} mt={2}>
                  <MDButton variant="gradient" color="success" onClick={() => guardar(false)} disabled={guardando || cargando || reglas.length === 0}>
                    Guardar
                  </MDButton>
                  <MDButton variant="gradient" color="warning" onClick={() => guardar(true)} disabled={guardando || cargando || ejecutando || reglas.length === 0}>
                    Guardar y aplicar a los casos
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={confirmarActivo} onClose={() => !cambiandoActivo && setConfirmarActivo(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{disparadorActivo ? "Desactivar el Disparador" : "Activar el Disparador"}</DialogTitle>
        <DialogContent dividers>
          <DialogContentText component="div">
            {disparadorActivo ? (
              <>
                Los gestores van a trabajar como antes: no van a ver el panel del caso asignado ni el botón «Pedir un caso» y el sistema deja de
                asignarles casos.
                <br />
                <br />
                Los casos que tengan asignados en este momento se <strong>liberan</strong> (quedan registrados en el informe). La modalidad de
                cada usuario, las reglas y los puntajes no se tocan: al volver a activarlo todo sigue como estaba.
              </>
            ) : (
              <>
                Los gestores van a ver el panel del caso asignado y el botón «Pedir un caso» en Gestión Pre-Judicial, y los que son Obligatorios o
                Mixtos van a recibir casos automáticamente.
                <br />
                <br />
                Antes de activarlo conviene tener calculados los puntajes y revisada la modalidad de cada usuario.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="error" onClick={() => setConfirmarActivo(false)} disabled={cambiandoActivo}>
            Cancelar
          </MDButton>
          <MDButton variant="gradient" color={disparadorActivo ? "warning" : "success"} onClick={cambiarActivo} disabled={cambiandoActivo}>
            {disparadorActivo ? "Desactivar" : "Activar"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={sinPuntajeAbierto} onClose={() => !iniciandoSinPuntaje && setSinPuntajeAbierto(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Casos cerrados sin puntaje</DialogTitle>
        <DialogContent dividers>
          {cargandoSinPuntaje && (
            <MDBox display="flex" justifyContent="center" py={3}>
              <CircularProgress size={28} />
            </MDBox>
          )}
          {!cargandoSinPuntaje && sinPuntaje && (
            <DialogContentText component="div">
              <MDTypography variant="button" fontWeight="medium" color="dark" display="block" mb={1}>
                Casos que hoy no tienen puntaje: {sinPuntaje.cerrados.toLocaleString("es-AR")} cerrados y{" "}
                {sinPuntaje.abiertos.toLocaleString("es-AR")} abiertos.
              </MDTypography>
              «Calcular pendientes» solo toma los casos abiertos. Esto calcula, <strong>una sola vez</strong>, el puntaje de todos los
              casos que no tienen ninguno, cerrados incluidos, con las reglas activas de hoy.
              <ul>
                <li>Los casos que ya tienen puntaje no se tocan.</li>
                <li>
                  Los casos cerrados no se vuelven a calcular después (ya no se trabajan): su puntaje no cambia si más adelante cambian
                  las reglas.
                </li>
                <li>
                  El puntaje de los casos cerrados se ve en Gestión Pre-Judicial y en el Excel, sin estrella: un caso cerrado no se puede
                  marcar como prioritario ni se le propone a ningún gestor.
                </li>
                <li>
                  Se hace en segundo plano y puede tardar varios minutos si hay muchos casos. El avance se ve en esta pantalla, y si se
                  corta se puede volver a pedir: retoma los que faltan.
                </li>
              </ul>
              {sinPuntajeTotal === 0 && <strong>No hay casos sin puntaje: no hace falta calcular nada.</strong>}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="error" onClick={() => setSinPuntajeAbierto(false)} disabled={iniciandoSinPuntaje}>
            Cancelar
          </MDButton>
          <MDButton
            variant="gradient"
            color="warning"
            onClick={calcularSinPuntaje}
            disabled={iniciandoSinPuntaje || cargandoSinPuntaje || !sinPuntaje || sinPuntajeTotal === 0 || ejecutando}
          >
            Calcular {sinPuntajeTotal > 0 ? sinPuntajeTotal.toLocaleString("es-AR") : ""} casos
          </MDButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: "100%" }}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default PuntajeReglaListar;
